import {
  Flex,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react'
import React, { useCallback, useMemo, useState } from 'react'
import { AudioPlayer } from '~/components/shared/AudioPlayer'
import { AudioPlayerVariant } from '~/components/shared/componentStyles/AudioPlayer.style'
import TranscriptAudioView from '~/pages/CallAnalytics/Transcription/TranscriptAudioView'
import { CallDetailsOverviewTab } from '~/pages/Calls/CallDetails/CallDetailsOverviewTab'
import { CallDetailsStatsTab } from '~/pages/Calls/CallDetails/CallDetailsStatsTab'
import { CallDetailsTranscriptTab } from '~/pages/Calls/CallDetails/CallDetailsTranscriptTab'
import { useGetCallTranscript } from '~/queries/callAnalyticsQueries'
import { useGetVoicemail } from '~/queries/voicemailQueries'
import { textStyleKeys } from '~/themes/textStyles'

import { getTimeDifferenceInMs } from '@mangovoice/core'
import { CallInstance } from '@mangovoice/types'

import { useCallsViewModelContext } from '../Calls.viewmodel'
import TranscriptProcessingPlaceholder from './TranscriptProcessingPlaceholder'

interface CallDetailsAnalyticsContentProps {
  callInstance: CallInstance
  initialTab?: TCallAnalyticsTab
  onRequestedTimestampChange?: (timestamp: number) => void
  initialTimestamp?: number
  playbackTimestampRef: React.MutableRefObject<number | undefined>
}

export type TCallAnalyticsTab = 'overview' | 'transcript' | 'stats'

export const TRANSCRIPT_SUMMARY_NOT_AVAILABLE =
  'No transcript summary available.'

const CallDetailsAnalyticsTab = ({ children }: { children: string }) => {
  return (
    <Tab
      textStyle={textStyleKeys.Body2}
      fontSize="14px"
      color="gray.1300"
      borderRadius="0.5rem"
      _hover={{
        bg: 'gray.300',
      }}
      _selected={{
        bg: 'gray.500',
      }}
      _focus={{ boxShadow: 'none' }}
      px="1.5rem"
      py="0.75rem"
      flex="1"
    >
      {children}
    </Tab>
  )
}

const tabs = ['overview', 'transcript', 'stats'] as const
export const CallDetailsAnalyticsContent = ({
  callInstance,
  onRequestedTimestampChange,
  initialTimestamp,
  playbackTimestampRef,
}: CallDetailsAnalyticsContentProps) => {
  const { state, actions } = useCallsViewModelContext()
  const tabIndex = useMemo(
    () =>
      state.selectedCallInitialTab
        ? tabs.indexOf(state.selectedCallInitialTab)
        : 0,
    [state.selectedCallInitialTab],
  )
  const setTabIndex = useCallback((index: number) => {
    actions.appendState({
      selectedCallInitialTab: tabs[index] as TCallAnalyticsTab,
    })
  }, [])

  const [requestedTimestamp, setRequestedTimestamp] = useState<number>(
    initialTimestamp ?? 0,
  )

  const [scrollPositions, setScrollPositions] = React.useState({})

  const scrollbarRef = React.useRef<HTMLDivElement | null>(null)

  const [hasBrokenScrollbar, setHasBrokenScrollbar] = React.useState(false)
  const [isAudioPlaying, setIsAudioPlaying] = useState(false)
  const [playerPosition, setPlayerPosition] = useState(0)

  const handlePlayStateChange = (isPlaying: boolean) => {
    setIsAudioPlaying(isPlaying)
  }
  const handlePlayerPositionChange = (position: number) => {
    playbackTimestampRef.current = position
    setPlayerPosition(position)
  }
  const handlePlay = (time?: number) => {
    setRequestedTimestamp(time ?? playerPosition)
    onRequestedTimestampChange?.(time ?? playerPosition)
    setIsAudioPlaying(true)
  }
  const handlePause = () => {
    setIsAudioPlaying(false)
  }

  const transcriptQuery = useGetCallTranscript(
    callInstance.pbx.id,
    callInstance.id,
  )

  const voicemailId = callInstance.voicemail_id
  const {
    data: voicemail,
    error: voicemailError,
    isLoading: isVoicemailLoading,
  } = useGetVoicemail({
    voicemailId: voicemailId || 0,
    options: { enabled: !!voicemailId },
  })

  // Add this effect to check for scrollbar
  React.useEffect(() => {
    const checkForScrollbar = () => {
      const userAgent = navigator.userAgent
      const isChrome = userAgent.match(/Chrome/i) !== null

      if (scrollbarRef.current && isChrome) {
        const hasVerticalScrollbar =
          scrollbarRef.current.scrollHeight > scrollbarRef.current.clientHeight
        setHasBrokenScrollbar(hasVerticalScrollbar)
      }
    }

    checkForScrollbar()
    // Add resize listener to check again if window size changes
    window.addEventListener('resize', checkForScrollbar)
    return () => window.removeEventListener('resize', checkForScrollbar)
  }, [transcriptQuery.data]) // Re-run when transcript data changes

  const transcriptSummary =
    transcriptQuery.data?.ConversationCharacteristics.ContactSummary
      ?.AutoGenerated.OverallSummary.Content

  const overallCustomerScore =
    transcriptQuery.data?.ConversationCharacteristics.Sentiment.OverallSentiment
      .CUSTOMER ?? 0
  const overallAgentScore =
    transcriptQuery.data?.ConversationCharacteristics.Sentiment.OverallSentiment
      .AGENT ?? 0

  const handleTabsChange = (index) => {
    // Save the current scroll position
    if (scrollbarRef.current) {
      const currentScrollPosition = scrollbarRef.current.scrollTop

      setScrollPositions((prev) => ({
        ...prev,
        [tabIndex]: currentScrollPosition,
      }))
    }

    // Update the tab index
    setTabIndex(index)
  }

  const answerTimestampInMs =
    callInstance && !callInstance.is_missed && callInstance.first_answered_at
      ? getTimeDifferenceInMs(
          new Date(callInstance.first_answered_at),
          new Date(callInstance.started_at),
        )
      : undefined

  // Restore scroll position when the active tab changes
  React.useEffect(() => {
    if (scrollbarRef.current) {
      const savedScrollPosition = scrollPositions[tabIndex] || 0
      scrollbarRef.current.scrollTop = savedScrollPosition
    }
  }, [tabIndex, scrollPositions])

  const panelPadding = hasBrokenScrollbar
    ? '1.25rem 0.65rem 1.25rem 1.25rem'
    : '1.25rem'
  return transcriptQuery.isLoading ? (
    <Flex p="1.5rem" w="full" flexDir="column" gap="1.25rem">
      <Skeleton h="5.625rem" />
      <Flex w="full" h="9.625rem" gap="1.25rem">
        <Skeleton flexGrow="1" />
        <Skeleton flexGrow="1" />
      </Flex>
      <Skeleton h="10.375rem" />
      <Skeleton h="7.75rem" />
    </Flex>
  ) : (
    <Flex flexDirection="column" flex="1" overflow="hidden">
      <Flex
        flex="0 1 auto"
        flexDirection="column"
        padding="1.25rem"
        paddingBottom="0"
        gap="1.25rem"
      >
        {!callInstance.recording_url ? (
          <div>No recording available</div>
        ) : // Conditional Rendering for Transcript Processing
        callInstance?.transcription_status === 'COMPLETED' ? (
          <TranscriptAudioView
            audioUri={callInstance.recording_url}
            callTranscript={transcriptQuery.data}
            onAudioPlayerPositionChange={handlePlayerPositionChange}
            onAudioPlayStateChange={handlePlayStateChange}
            requestedTimestamp={requestedTimestamp}
            requestedIsPlaying={isAudioPlaying}
          />
        ) : (
          <AudioPlayer
            variant={AudioPlayerVariant.Recording}
            audioUri={callInstance.recording_url}
            showJumpToAnswerButton={false}
            showInfoButton={false}
            showInlineInfoButton={true}
            requestedTimestamp={requestedTimestamp}
            requestedIsPlaying={isAudioPlaying}
            onValueChanged={handlePlayerPositionChange}
            onPlayStateChange={handlePlayStateChange}
          />
        )}

        <Tabs
          index={tabIndex}
          onChange={handleTabsChange}
          marginLeft="-1.25rem"
          marginRight="-1.25rem"
        >
          <TabList
            display="flex"
            justifyContent="space-evenly"
            gap="0.25rem"
            borderBottom="2px solid"
            height="2.875rem"
            borderColor="gray.400"
            width="full"
            padding="0 1.25rem"
            paddingBottom="0.75rem"
            onKeyDown={(event) => {
              const keysToPrevent = ['ArrowLeft', 'ArrowRight']
              if (keysToPrevent.includes(event.key)) {
                event.preventDefault()
              }
            }}
          >
            <CallDetailsAnalyticsTab>Overview</CallDetailsAnalyticsTab>
            <CallDetailsAnalyticsTab>Transcripts</CallDetailsAnalyticsTab>
            <CallDetailsAnalyticsTab>Stats</CallDetailsAnalyticsTab>
          </TabList>
        </Tabs>
      </Flex>
      <Flex
        flex="1"
        flexDir={'column'}
        overflow="scroll"
        minHeight="0"
        ref={scrollbarRef}
      >
        <Tabs index={tabIndex}>
          <TabPanels maxHeight="10vh">
            {/* Overview Tab Panel */}
            <TabPanel padding={panelPadding}>
              <CallDetailsOverviewTab
                callInstance={callInstance}
                answerTimestampInMs={answerTimestampInMs}
                transcriptSummary={
                  transcriptSummary ?? TRANSCRIPT_SUMMARY_NOT_AVAILABLE
                }
                overallCustomerScore={overallCustomerScore}
                overallAgentScore={overallAgentScore}
                isLoadingTranscripts={transcriptQuery.isLoading}
                voicemail={voicemail}
                voicemailError={voicemailError ? voicemailError : undefined}
                isVoicemailLoading={isVoicemailLoading}
              />
            </TabPanel>
            {/* Transcript Tab Panel */}
            <TabPanel padding={panelPadding}>
              {callInstance?.transcription_status !== 'COMPLETED' ? (
                <TranscriptProcessingPlaceholder isInTab={true} />
              ) : (
                <CallDetailsTranscriptTab
                  transcript={transcriptQuery.data}
                  isLoadingTranscripts={transcriptQuery.isLoading}
                  playerPosition={playerPosition}
                  onPlay={handlePlay}
                  onPause={handlePause}
                  isAudioPlaying={isAudioPlaying}
                />
              )}
            </TabPanel>
            {/* Stats Tab Panel */}
            <TabPanel padding={panelPadding}>
              {callInstance?.transcription_status !== 'COMPLETED' ? (
                <TranscriptProcessingPlaceholder isInTab={true} />
              ) : (
                <CallDetailsStatsTab
                  transcript={transcriptQuery.data}
                  isLoadingTranscripts={transcriptQuery.isLoading}
                />
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Flex>
    </Flex>
  )
}
